'use client'

import { Button, html } from '@ensemble.music/ui'
import { Menu } from 'lucide-react'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { useState } from 'react'
import { LogoLink } from '../wizard/WizardShell'
import { navSections } from './content'
import { MobileMenu } from './MobileMenu'
import { useScrollSpy } from './useScrollSpy'

const sectionIds = navSections.map((section) => section.id)

export function Navbar({ onStart }: { onStart: () => void }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const active = useScrollSpy(sectionIds)
  const { scrollY } = useScroll()

  // Position, height and the 200 ms shrink live in the .landing-nav rule in style.css: the
  // Tailwind frontend has no transition utilities, and CSS does this without a re-render.
  useMotionValueEvent(scrollY, 'change', (value) => setScrolled(value > 8))

  return (
    <header className={`landing-nav${scrolled ? ' landing-nav--scrolled' : ''}`}>
      <html.a
        href="#inhalt"
        className="landing-skip bg-primary text-on-primary"
      >
        Zum Inhalt springen
      </html.a>

      <html.div
        className={`flex flex-row w-full h-full bg-background border-b ${
          scrolled ? 'border-border-color shadow-sm' : 'border-transparent'
        }`}
      >
        <html.div className="flex flex-row items-center justify-between gap-4 w-full h-full max-w-[1120px] mx-auto px-8 max-sm:px-4">
          <LogoLink
            href="#inhalt"
            label="zum Seitenanfang"
          />

          <html.nav
            aria-label="Hauptnavigation"
            className="flex flex-row items-center gap-1 max-md:hidden"
          >
            {navSections.map((section) => (
              <html.a
                key={section.id}
                href={`#${section.id}`}
                aria-current={active === section.id ? 'true' : undefined}
                className={`flex flex-row items-center min-h-[44px] px-3 rounded-full text-base no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring ${
                  active === section.id
                    ? 'bg-primary-soft text-primary-text font-semibold'
                    : 'text-color-secondary hover:bg-background-subtle'
                }`}
              >
                {section.label}
              </html.a>
            ))}
          </html.nav>

          <html.div className="flex flex-row items-center gap-2">
            {/* below 340 px the label would wrap: there the hero CTA, the sticky bar and the
                sheet all still offer the same action, so the bar drops it instead */}
            <Button
              type="button"
              className="max-xxs:hidden whitespace-nowrap"
              onClick={() => onStart()}
            >
              Anfrage starten
            </Button>
            <Button
              type="button"
              tone="secondary"
              className="hidden max-md:inline-flex"
              aria-label="Menü öffnen"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <Menu
                size={20}
                aria-hidden="true"
              />
            </Button>
          </html.div>
        </html.div>
      </html.div>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onStart={onStart}
      />
    </header>
  )
}
